import { useState } from 'react';
import OnboardingLayout from './OnboardingLayout';
import { getClinicDetails, setClinicDetails } from '../../Utils/clinicAuth';
import { updateClinicProfile } from '../../Services/APIs/authentication';
import { buildCompany, extractEditableSlug, getClaraviewLink, SLUG_MAX_LENGTH, validateCompanySlug } from '../../Utils/claraviewLink';
import ConfirmDialog from '../../Components/UI/ConfirmDialog';

const APP_DOMAIN = 'https://app.claraview.ai/';

const SHARING_IDEAS = [
    'Add to your email signature',
    'Post on practice social media',
    'Include in appointment reminders',
    'Display as a QR code in your waiting room',
];

/* Same color-mix() approach as the other v2 pages — Tailwind's opacity modifier doesn't work on
 * our var(--cv2-*)-based custom colors. */
const SCOPED_STYLES = `
  .cv2-share-eyebrow { color: color-mix(in srgb, var(--cv2-foreground) 28%, transparent); }
  .cv2-share-subcopy { color: color-mix(in srgb, var(--cv2-foreground) 38%, transparent); }
  .cv2-share-row { border-color: color-mix(in srgb, var(--cv2-foreground) 8%, transparent); }
  .cv2-share-label { color: color-mix(in srgb, var(--cv2-foreground) 30%, transparent); }
  .cv2-share-action { color: color-mix(in srgb, var(--cv2-foreground) 50%, transparent); transition: color 0.15s ease; }
  .cv2-share-action:hover { color: #A1B3A8; } /* claraview-secondary */
  .cv2-share-toggle { color: color-mix(in srgb, var(--cv2-foreground) 30%, transparent); transition: color 0.15s ease; }
  .cv2-share-toggle:hover { color: color-mix(in srgb, var(--cv2-foreground) 55%, transparent); }
  .cv2-share-slug-prefix { color: color-mix(in srgb, var(--cv2-foreground) 35%, transparent); }
  .cv2-share-slug-input { border-color: color-mix(in srgb, var(--cv2-foreground) 20%, transparent); }
  .cv2-share-slug-input::placeholder { color: color-mix(in srgb, var(--cv2-foreground) 20%, transparent); }
  .cv2-share-ideas-label { color: color-mix(in srgb, var(--cv2-foreground) 25%, transparent); }
  .cv2-share-idea { color: color-mix(in srgb, var(--cv2-foreground) 42%, transparent); }
  .cv2-share-error-banner {
    color: var(--cv2-destructive);
    background-color: color-mix(in srgb, var(--cv2-destructive) 10%, transparent);
    border-color: color-mix(in srgb, var(--cv2-destructive) 30%, transparent);
  }
`;

const getFallbackSlug = (doctorName) => {
    if (!doctorName) return '';
    return doctorName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
};

const Share = ({ onBack, onNext }) => {
    const clinicDetails = getClinicDetails();
    const savedSlug = extractEditableSlug(clinicDetails?.company) || getFallbackSlug(clinicDetails?.doctor_name);
    const savedLink = getClaraviewLink(clinicDetails?.company);

    const [isEditing, setIsEditing] = useState(false);
    const [slug, setSlug] = useState(savedSlug);
    const [linkError, setLinkError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(savedLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleEdit = () => {
        setSlug(savedSlug);
        setLinkError('');
        setIsEditing(true);
    };

    const handleCancel = () => {
        setSlug(savedSlug);
        setLinkError('');
        setShowConfirm(false);
        setIsEditing(false);
    };

    const handleSlugChange = (e) => setSlug(e.target.value.toLowerCase());

    const handleRequestSave = () => {
        const error = validateCompanySlug(slug);
        if (error) {
            setLinkError(error);
            return;
        }
        setLinkError('');
        setShowConfirm(true);
    };

    const handleSave = async () => {
        setShowConfirm(false);
        setIsSaving(true);
        setLinkError('');
        try {
            const company = buildCompany(slug);
            const response = await updateClinicProfile({ company });
            setClinicDetails({ ...clinicDetails, ...(response.data?.data || { company }) });
            setIsEditing(false);
        } catch (error) {
            setLinkError(error?.response?.data?.message || error?.data?.message || 'Failed to update link');
        }
        setIsSaving(false);
    };

    return (
        <OnboardingLayout stepKey="share" onBack={onBack}>
            <style>{SCOPED_STYLES}</style>
            <div className="flex-1 flex items-center justify-center px-7 sm:px-12 py-[72px]">
                <div className="max-w-[440px] w-full">
                    <div className="mb-12">
                        <p className="cv2-share-eyebrow font-spaceMono text-[9px] tracking-[1.4px] uppercase mb-4">
                            Step 2 of 2
                        </p>
                        <h2 className="font-aeonik font-normal text-[38px] leading-[44px] tracking-[-0.9px] text-cv2-foreground mb-[10px] normal-case">
                            Your Simulation Link
                        </h2>
                        <p className="cv2-share-subcopy font-aeonik text-sm leading-[22px] normal-case">
                            Share this so patients can request a smile preview before their visit.
                        </p>
                    </div>

                    {linkError && (
                        <div className="cv2-share-error-banner text-sm border rounded-md px-3 py-2 mb-4">
                            {linkError}
                        </div>
                    )}

                    <div className="mb-2">
                        <p className="cv2-share-label font-spaceMono text-[10px] tracking-[0.8px] uppercase mb-[10px]">Your link</p>
                        <div className="cv2-share-row border-t" />
                        <div className="flex items-center justify-between gap-4 py-[18px]">
                            <p className="font-aeonik text-base text-cv2-foreground normal-case select-all cursor-text truncate" title={savedLink}>
                                {savedLink}
                            </p>
                            <button type="button" onClick={handleCopy} className="cv2-share-action shrink-0 font-spaceMono text-[10px] tracking-[0.8px] uppercase">
                                {copied ? 'Copied ✓' : 'Copy →'}
                            </button>
                        </div>
                        <div className="cv2-share-row border-t pb-3.5 pt-3 flex items-center gap-5">
                            <button type="button" onClick={isEditing ? handleCancel : handleEdit} className="cv2-share-toggle font-spaceMono text-[10px] tracking-[0.8px] uppercase">
                                {isEditing ? 'Cancel' : 'Change link'}
                            </button>
                        </div>
                    </div>

                    {isEditing && (
                        <div className="cv2-share-row mt-4 border-t pt-4">
                            <p className="cv2-share-label font-spaceMono text-[9px] tracking-[1.2px] uppercase mb-[7px]">Custom slug</p>
                            <div className="flex items-center gap-2.5">
                                <span className="cv2-share-slug-prefix font-aeonik text-[13px] shrink-0">{APP_DOMAIN}</span>
                                <input
                                    type="text"
                                    value={slug}
                                    onChange={handleSlugChange}
                                    autoFocus
                                    maxLength={SLUG_MAX_LENGTH}
                                    placeholder="yourname"
                                    className="cv2-share-slug-input flex-1 bg-transparent border-0 border-b outline-none font-aeonik text-[15px] text-cv2-foreground pb-1 normal-case"
                                />
                                <button
                                    type="button"
                                    onClick={handleRequestSave}
                                    disabled={isSaving}
                                    className="cv2-share-action shrink-0 font-spaceMono text-[10px] tracking-[0.8px] uppercase disabled:opacity-50"
                                >
                                    {isSaving ? 'Saving…' : 'Save'}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="mt-9 mb-9">
                        <p className="cv2-share-ideas-label font-spaceMono text-[9px] tracking-[1px] uppercase mb-3.5">Ideas for sharing</p>
                        <div className="flex flex-col gap-2.5">
                            {SHARING_IDEAS.map((tip) => (
                                <p key={tip} className="cv2-share-idea font-aeonik text-sm leading-5 normal-case">{tip}</p>
                            ))}
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onNext}
                        className="inline-flex items-center h-11 px-7 bg-claraview-secondary hover:brightness-110 transition-all rounded-[3px] font-spaceMono text-[11px] tracking-[1.2px] text-claraview-quaternary uppercase"
                    >
                        Finish Setup
                    </button>
                </div>
            </div>

            {showConfirm && (
                <ConfirmDialog
                    title="Change your link?"
                    message="Any link or QR code you've already shared with patients will stop working, and another clinic can claim your old link."
                    confirmLabel="Change Link"
                    cancelLabel="Keep Current"
                    onConfirm={handleSave}
                    onCancel={() => setShowConfirm(false)}
                />
            )}
        </OnboardingLayout>
    );
};

export default Share;
